import z from 'zod';

/**
 * FhirResourceType - All FHIR R4B resource types
 * Used for type-safe resource type references
 */
export const FhirResourceTypeSchema = z.enum([
  'Account',
  'ActivityDefinition',
  'AdministrableProductDefinition',
  'AdverseEvent',
  'AllergyIntolerance',
  'Appointment',
  'AppointmentResponse',
  'AuditEvent',
  'Basic',
  'Binary',
  'BiologicallyDerivedProduct',
  'BodyStructure',
  'Bundle',
  'CapabilityStatement',
  'CarePlan',
  'CareTeam',
  'CatalogEntry',
  'ChargeItem',
  'ChargeItemDefinition',
  'Citation',
  'Claim',
  'ClaimResponse',
  'ClinicalImpression',
  'ClinicalUseDefinition',
  'CodeSystem',
  'Communication',
  'CommunicationRequest',
  'CompartmentDefinition',
  'Composition',
  'ConceptMap',
  'Condition',
  'Consent',
  'Contract',
  'Coverage',
  'CoverageEligibilityRequest',
  'CoverageEligibilityResponse',
  'DetectedIssue',
  'Device',
  'DeviceDefinition',
  'DeviceMetric',
  'DeviceRequest',
  'DeviceUseStatement',
  'DiagnosticReport',
  'DocumentManifest',
  'DocumentReference',
  'Encounter',
  'Endpoint',
  'EnrollmentRequest',
  'EnrollmentResponse',
  'EpisodeOfCare',
  'EventDefinition',
  'Evidence',
  'EvidenceReport',
  'EvidenceVariable',
  'ExampleScenario',
  'ExplanationOfBenefit',
  'FamilyMemberHistory',
  'Flag',
  'Goal',
  'GraphDefinition',
  'Group',
  'GuidanceResponse',
  'HealthcareService',
  'ImagingStudy',
  'Immunization',
  'ImmunizationEvaluation',
  'ImmunizationRecommendation',
  'ImplementationGuide',
  'Ingredient',
  'InsurancePlan',
  'Invoice',
  'Library',
  'Linkage',
  'List',
  'Location',
  'ManufacturedItemDefinition',
  'Measure',
  'MeasureReport',
  'Media',
  'Medication',
  'MedicationAdministration',
  'MedicationDispense',
  'MedicationKnowledge',
  'MedicationRequest',
  'MedicationStatement',
  'MedicinalProductDefinition',
  'MessageDefinition',
  'MessageHeader',
  'MolecularSequence',
  'NamingSystem',
  'NutritionOrder',
  'NutritionProduct',
  'Observation',
  'ObservationDefinition',
  'OperationDefinition',
  'OperationOutcome',
  'Organization',
  'OrganizationAffiliation',
  'PackagedProductDefinition',
  'Parameters',
  'Patient',
  'PaymentNotice',
  'PaymentReconciliation',
  'Person',
  'PlanDefinition',
  'Practitioner',
  'PractitionerRole',
  'Procedure',
  'Provenance',
  'Questionnaire',
  'QuestionnaireResponse',
  'RegulatedAuthorization',
  'RelatedPerson',
  'RequestGroup',
  'ResearchDefinition',
  'ResearchElementDefinition',
  'ResearchStudy',
  'ResearchSubject',
  'RiskAssessment',
  'Schedule',
  'SearchParameter',
  'ServiceRequest',
  'Slot',
  'Specimen',
  'SpecimenDefinition',
  'StructureDefinition',
  'StructureMap',
  'Subscription',
  'SubscriptionStatus',
  'SubscriptionTopic',
  'Substance',
  'SubstanceDefinition',
  'SupplyDelivery',
  'SupplyRequest',
  'Task',
  'TerminologyCapabilities',
  'TestReport',
  'TestScript',
  'ValueSet',
  'VerificationResult',
  'VisionPrescription',
]);

export type FhirResourceType = z.infer<typeof FhirResourceTypeSchema>;

/**
 * AnswerOptionSource - Configuration for dynamically loading answer options from Zambdas
 */
export const AnswerOptionSourceSchema = z.discriminatedUnion('zambdaId', [
  z.object({
    zambdaId: z.literal('get-answer-options'),
    resourceType: FhirResourceTypeSchema,
    query: z.string(),
    /*
     * When included, the identifier value with a system matching the prependedIdentifier
     * will be prepended to the display name of the returned resource, separated by ' - '
     * For example, if prependedIdentifier is {system_for_NPIs} and the resourceType is Practitioner,
     * the returned display name will be '{NPI_value} - {practitioner_name}'
     */
    prependedIdentifier: z.string().optional(),
  }),
  z.object({
    zambdaId: z.literal('get-all-insurance-payers'),
    prependIdentifier: z.boolean().optional(),
  }),
  z.object({
    zambdaId: z.literal('get-patient-insurance-payers'),
  }),
]);

export type AnswerOptionSource = z.infer<typeof AnswerOptionSourceSchema>;

/**
 * AnswerLoadingOptions - Configuration for how to load answer options
 */
export const AnswerLoadingOptionsSchema = z.object({
  strategy: z.enum(['prefetch', 'dynamic']),
  answerSource: AnswerOptionSourceSchema.optional(),
});

export type AnswerLoadingOptions = z.infer<typeof AnswerLoadingOptionsSchema>;
